// Categorizes one merged person: investor type, sectors, check size, private holds, why-now signals.
//
// shared/ingest.ts calls categorize() for every person in a build. The rules are plain data below, and every tag
// carries the words that produced it, so Peter can see why and fix the source. Flag keys are the ones the rest of
// the app knows (network.ts Flag, SENSITIVE_WINDOW_DAYS); "tapped out" stays its own flag, held for a year.
// Pure: no DOM, no Node APIs. Runs in the browser, the Worker and the tests.

import { INVESTOR_TYPES, SECTORS } from "./network";

export interface CategorizeInput {
  title?: string;
  company?: string;
  /** All of Peter's notes for this person, joined (Monday notes, Google notes, spreadsheet comments). */
  notes?: string;
  /** The CRM's own investor-type cell, however messy ("fam office", "IS", "UHNW"). */
  crmType?: string;
  /** The CRM's sector cell(s), joined. */
  crmSectors?: string;
  checkMinRaw?: string | number | null;
  checkMaxRaw?: string | number | null;
  /** A single free-text check-size cell ("$1-5M", "up to 10mm"). */
  checkRaw?: string;
  /** CRM status ("Active", "Inactive", "Do Not Contact"). */
  status?: string;
  mondayGroup?: string;
  /** Conference registration types ("Family Office", "Sponsor", "LP / Investor"). */
  registrationTypes?: string[];
  /** Conference "areas of interest" and similar self-reported interests. */
  interests?: string[];
  /** Sectors this person asked Peter for deals in (from the touch log). */
  askedSectors?: string[];
  /** Sectors of Peter's past deals this person invested in. */
  investedSectors?: string[];
  /** Touch-log lines, read only for private signals ("chemo starts next week"). */
  recentTouches?: { date: string; summary: string }[];
}

export type FieldSource = "crm" | "notes" | "claude" | "missing";

/** A life event or status, with the words that triggered it. For a touch, evidence is the touch's summary. */
export interface Signal {
  key: string;
  label: string;
  evidence: string;
  date: string | null;
}

export interface CategorizeResult {
  type: string;
  sectors: string[];
  /** Dollars, 0 when unknown. When nothing states a check, these are the type's usual range and
   *  sources.check says "estimated from type". */
  checkMin: number;
  checkMax: number;
  /** From a stated check only: CHECK_BANDS. */
  checkBand: string;
  /** Reasons to leave someone alone: sensitive life events (see SENSITIVE_WINDOW_DAYS), "retired", "tapped out". */
  holds: Signal[];
  /** "just invested" or "burned" in one sector. */
  sectorHolds: { sector: string; kind: string; evidence: string; date?: string | null }[];
  /** Good reasons to call: "sold company", "new role", "family milestone". */
  whyNow: Signal[];
  /** Standing dislikes from the notes: "no real estate", "no venture", "no funds". */
  negativePrefs: string[];
  evidence: { type: string; sectors: string };
  sources?: { type: FieldSource; sectors: FieldSource; check: string };
}

/** People who write checks themselves. Bankers, advisors and service providers refer; they don't invest. */
export const CAPITAL_TYPES: ReadonlySet<string> = new Set([
  "Family office", "Multi-family office", "UHNW individual", "HNW individual", "Independent sponsor", "PE fund GP", "LP / fund allocator",
]);
const NOT_INVESTORS = new Set(["Banker / advisor", "Service provider"]);

export const CHECK_BANDS = ["<$500K", "$500K-$2M", "$2M-$10M", "$10M+", "Not an investor", "Unknown"] as const;
export type CheckBand = (typeof CHECK_BANDS)[number];

/** The usual check range by type, used when nothing states one (the deal screen needs a number to gate on). */
export const TYPE_DEFAULT_CHECK: Record<string, [number, number]> = {
  "Family office": [1e6, 1e7], "Multi-family office": [5e5, 5e6], "UHNW individual": [2.5e5, 3e6], "HNW individual": [1e5, 5e5],
  "Independent sponsor": [2.5e5, 1e6], "PE fund GP": [2e6, 2e7], "LP / fund allocator": [5e6, 3e7], "Operator / executive": [1e5, 5e5],
};

/* ------------------------------------------------------------------ rules (plain data, tune freely) */

const SECTOR_RULES: Record<string, RegExp[]> = {
  "Aerospace & Defense": [/\baero/i, /aviation/i, /airline/i, /aircraft/i, /\be?vtol\b/i, /\bdrones?\b/i, /\bdefen[cs]e\b(?! attorney| lawyer| counsel)/i, /king air/i, /\bmro\b/i],
  "Food & Beverage": [/\bfood\b(?! distribution| logistics)/i, /beverage/i, /\bf&b\b/i, /food and bev/i, /oat milk/i, /\bwiner(?:y|ies)\b/i, /\bwine\b/i, /\bdistill/i, /spirits brand/i, /\bbrew(?:ery|eries|ing compan|pub)/i, /co-?packer/i, /snack/i, /dairy/i],
  "CPG": [/\bcpg\b/i, /consumer packaged/i, /consumer brands/i, /loves brands/i, /pet food/i],
  "Restaurants & Hospitality": [/restaurant/i, /hospitality/i, /\bhotels?\b/i, /\bqsr\b/i, /franchis/i, /\bchef\b/i, /wendy'?s/i],
  "Food Logistics & Cold Chain": [/cold chain/i, /cold storage/i, /trucking/i, /food distribution/i, /food logistics/i, /refrigerated/i],
  "Healthcare Services": [/health ?care/i, /health services/i, /home health/i, /behavioral health/i, /(?<!\b(?:in|at|to|from|after|left|leaving) (?:the )?)\bhospitals?\b(?! stay| visit| bed| room)/i, /\bdoc,/i, /\bdoctors?\b/i, /physician/i, /\bmedical\b(?! leave| issue| scare| bills?)/i, /dental/i, /\bclinics?\b/i],
  "Business Services": [/business services/i, /b2b services/i, /staffing/i, /\bhvac\b/i, /outsourc/i, /facilities services/i],
  "Tech-Enabled Services": [/tech-?enabled/i, /software-?ish services/i],
  "Software": [/software(?!-?ish)/i, /\bsaas\b/i, /recurring revenue/i],
  "Industrials & Manufacturing": [/industrial/i, /manufactur/i, /machine shop/i, /metal bend/i, /packaging/i, /fabricat/i],
  "Consumer Services": [/consumer services/i, /\bgyms?\b/i, /car wash/i, /consumer stuff/i, /fitness/i],
  "Automotive": [/\bauto\b/i, /automotive/i, /car guy/i, /auto parts/i, /dealerships?/i],
  "Energy": [/energy/i, /\boil\b/i, /\bnatural gas\b/i, /oil and gas/i, /permian/i, /solar/i, /midstream/i],
  "Sports & Recreation": [/sports? team/i, /golf and ski/i, /\bsports\b/i, /recreation/i, /ski business/i],
  "Legal & Professional Services": [/law firm/i, /\blegal\b/i, /professional services/i, /litigat/i, /\bmso\b/i],
};

/** Ordered: the first type whose pattern fires wins, so the specific ones come first. */
const TYPE_RULES: [string, RegExp[]][] = [
  ["Multi-family office", [/multi[- ]?family office/i, /\bmfo\b/i, /multi fo\b/i, /runs money for \d+ families/i, /private wealth/i, /wealth partners/i, /family advisors/i]],
  ["Family office", [/single[- ]?family office/i, /\bsfo\b/i, /family office/i, /fam office/i, /^fo$/i, /\bfo\b(?!r)/i, /patriarch/i, /family invest/i]],
  ["Independent sponsor", [/independent sponsor/i, /fundless sponsor/i, /^is$/i, /does (?:his|her|their) own deals/i, /^sponsor$/i, /search fund/i]],
  ["LP / fund allocator", [/\blp\b/i, /allocator/i, /endowment/i, /funds only/i, /only does funds/i, /fund of funds/i, /pension/i]],
  ["PE fund GP", [/pe fund gp/i, /^gp$/i, /fund gp/i, /pe partner/i, /private equity/i, /^pe$/i, /\bpe fund\b/i, /co-invest sleeve/i, /growth equity/i]],
  ["UHNW individual", [/\buhnwi?\b/i, /ultra[- ]high/i, /rich guy/i, /big personal checks/i, /billionaire/i]],
  // someone who runs a company and also writes angel checks is an operator first
  ["Operator / executive", [/runs a company/i, /ceo type/i, /\boperator\b/i]],
  ["Banker / advisor", [/banker/i, /wealth advisor/i, /referral source/i, /sends me clients/i, /financial advisor/i, /\bria\b/i, /investment bank/i, /^advisor$/i]],
  ["Service provider", [/service provider/i, /^svc$/i, /accountant/i, /accounting/i, /\blawyer\b/i, /attorney/i, /\bcpa\b/i, /not an investor/i, /good referrals/i, /\bllp\b/i, /law group/i, /of counsel/i, /consulting/i]],
  ["HNW individual", [/\bhnwi?\b/i, /high[- ]net/i, /accredited/i, /small checks/i, /angel/i, /^indiv/i, /private investor/i]],
  // a former CEO or a chairman is usually investing personal money now
  ["UHNW individual", [/\b(?:former|retired|ex-?) ?(?:ceo|president|chairman|founder|coo|cfo)\b/i, /^chairman/i]],
  ["Operator / executive", [/(?<!ex-|former |retired )\bceo\b/i, /^exec/i, /\bpresident\b/i, /\bcoo\b/i, /\bcfo\b/i, /\bfounder\b/i]],
];

const GROUP_HINTS: [RegExp, string][] = [
  [/multi[- ]?family/i, "Multi-family office"], [/family office/i, "Family office"], [/independent sponsor/i, "Independent sponsor"],
  [/sponsor|\bgp/i, "PE fund GP"], [/\blps?\b|allocator|endowment/i, "LP / fund allocator"], [/uhnw|hnw|individual/i, "HNW individual"],
  [/operator|exec/i, "Operator / executive"], [/banker|advisor/i, "Banker / advisor"], [/service/i, "Service provider"],
];
const REGISTRATION_HINTS: [RegExp, string][] = [
  [/family office/i, "Family office"], [/sponsor/i, "Independent sponsor"], [/service/i, "Service provider"],
  [/\blp\b|investor/i, "HNW individual"], [/\bgp\b|fund/i, "PE fund GP"],
];

interface Rule {
  key: string;
  label: string;
  rx: RegExp[];
  /** Skip a match when the words just before it say it happened to someone else ("his father passed away"). */
  selfOnly?: boolean;
}

const HOLD_RULES: Rule[] = [
  { key: "deceased", label: "Deceased", rx: [/\bpassed away\b/i, /\bdeceased\b/i, /\bRIP\b/, /\bin memoriam\b/i], selfOnly: true },
  { key: "loss of child", label: "Lost a child", rx: [/\blost (?:his|her|their) (?:son|daughter|child|boy|girl)\b/i, /\b(?:son|daughter|child) (?:passed|died)\b/i] },
  {
    key: "serious illness",
    label: "Serious illness",
    rx: [
      /\b(?:battling|fighting|beat|beating|has|had|treatment for|diagnosed with|survivor of)\s+(?:\w+\s+){0,2}cancer\b/i, /\bcancer (?:diagnosis|treatment|scare|again)\b/i,
      /\bchemo(?:therapy)?\b(?! infusion| centers?| clinics?| compan)/i, /\bdiagnosed\b/i, /\bhospice\b(?! compan| business| operator| roll| platform| deal)/i,
      /\bstent\b/i, /\bhealth scare\b/i, /\bheart attack\b/i, /\bin (?:the )?(?:hospital|icu|rehab)\b/i, /\bin (?:our |my )?prayers\b/i,
    ],
    selfOnly: true,
  },
  {
    key: "divorce",
    label: "Going through a divorce",
    rx: [/\bdivorc(?:e|ed|ing)\b(?! attorney| lawyer| law\b| practice| firm| mediat)/i, /\b[Ss]eparated from (?:(?:his|her|their) (?:wife|husband|spouse)|[A-Z][a-z]+\b)/, /\bsplit (?:up )?(?:with|from) (?:his|her|their) (?:wife|husband|spouse)\b/i],
  },
  { key: "opted out", label: "Asked not to be contacted", rx: [/\bdo not (?:contact|call|email)\b/i, /\bdon'?t (?:call|contact|email) (?:him|her|them|me)\b/i, /\bdnc\b/i, /\bremove (?:me|him|her|them) from\b/i, /\bunsubscribe/i] },
  {
    key: "retired",
    label: "Done with private deals",
    rx: [
      /\bretired\s+(?:in\s+)?(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{4}/i, /\bdone with (?:private )?(?:deals|investing|private markets)\b/i,
      /\bwinding down\b/i, /\bout of the (?:deal|investing) (?:business|game)\b/i, /\bno longer (?:investing|doing deals|writing checks)\b/i, /\bretired from (?:investing|deals|private)/i,
    ],
  },
  { key: "tapped out", label: "Tapped out", rx: [/\btapped out\b/i, /\bfully (?:allocated|deployed|committed)\b/i, /\bsitting out\b/i, /\bno dry powder\b/i, /\bout of dry powder\b/i] },
];

const WHY_NOW_RULES: Rule[] = [
  {
    key: "sold company",
    label: "Fresh liquidity",
    rx: [/\bsold (?:his|her|their|the) (?:company|business|firm)\b/i, /\bexited\b[^.;|]{0,40}\b(?:deal|company|business|stake)\b/i, /\bsitting on cash\b/i, /\blooking to redeploy\b/i, /\bliquidity (?:coming|event)\b/i, /\bgot liquidity\b/i],
  },
  { key: "new role", label: "New role", rx: [/\bnew role\b/i, /\bpromoted to\b/i, /\bjoined [^.;|]{1,40} as\b/i, /\bnew job\b/i, /\bjust started at\b/i] },
  { key: "family milestone", label: "Family milestone", rx: [/\bnew grand(?:child|baby|son|daughter)\b/i, /\bgrandchild\b/i, /\b(?:got|getting) married\b/i, /\bnew baby\b/i] },
];

const SECTOR_HOLD_RULES: ["just invested" | "burned", RegExp][] = [
  ["just invested", /\bjust (?:wrote a check|invested|committed|put money) in(?:to)? an? ([^.;|()]+?) deal/gi],
  ["burned", /\b(?:got burned|lost (?:money|his shirt|her shirt|a lot)) on an? ([^.;|()]+?) deal/gi],
];

const NEGATIVE_PREFS: [string, RegExp][] = [
  ["no real estate", /\b(?:hates|no|won'?t do|doesn'?t do|avoids?|never does) real estate\b/i],
  ["no venture", /\b(?:no|hates|won'?t do|doesn'?t do|never does) (?:venture|vc|startups?|early[- ]stage)\b/i],
  ["no funds", /\b(?:no|won'?t do|doesn'?t do|never does) (?:blind[- ]pool )?funds\b/i],
  ["no crypto", /\bno crypto\b/i],
  ["no cannabis", /\bno cannabis\b/i],
];

const RELATIVE_BEFORE = /\b(?:father|mother|mom|dad|wife|husband|spouse|brother|sister|son|daughter|child|kid|parent|uncle|aunt|grand\w*|in-law|cousin|friend|partner|dog|cat|boss|ceo|founder)'?s?\b[^.;|]{0,14}$/i;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// The name itself counts too ("Software" but not "software-ish").
const SECTOR_RX: [string, RegExp[]][] = SECTORS.map((s) => [s, [...(SECTOR_RULES[s] ?? []), new RegExp(`\\b${escapeRe(s)}\\b(?!-)`, "i")]]);
const TYPE_RX: [string, RegExp[]][] = TYPE_RULES.map(([t, rs]) => [t, [...rs, new RegExp(escapeRe(t), "i")]]);
const TYPE_SET = new Set(INVESTOR_TYPES);

/* ------------------------------------------------------------------ helpers */

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** "Passed away Mar 2026" -> "2026-03-15". Looks in the snippet only. */
export function monthDate(text: string): string | null {
  const m = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.? (\d{4})\b/i.exec(text);
  if (!m) return null;
  return `${m[2]}-${String(MONTHS.indexOf(m[1].toLowerCase()) + 1).padStart(2, "0")}-15`;
}

/** The sentence around a match, for evidence. */
function around(text: string, start: number, end: number): string {
  const left = Math.max(text.lastIndexOf(".", start - 1), text.lastIndexOf("|", start - 1), text.lastIndexOf(";", start - 1)) + 1;
  const stops = [".", "|", ";"].map((c) => text.indexOf(c, end)).filter((i) => i >= 0);
  const right = stops.length ? Math.min(...stops) : text.length;
  return text.slice(left, right).trim().slice(0, 160);
}

const MONEY = /\$?\s*(\d[\d,]*(?:\.\d+)?)\s*(k|thousand|mm|m|mil|million|bn|b|billion)?\b/gi;
const UNIT: Record<string, number> = { k: 1e3, thousand: 1e3, m: 1e6, mm: 1e6, mil: 1e6, million: 1e6, b: 1e9, bn: 1e9, billion: 1e9 };

/** A dollar amount: 250000, "$250,000", "$1.5M", "2mm". Bare numbers are taken as dollars. */
export function parseMoney(v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  MONEY.lastIndex = 0;
  const m = MONEY.exec(String(v));
  MONEY.lastIndex = 0;
  if (!m) return null;
  const x = parseFloat(m[1].replace(/,/g, "")) * (UNIT[(m[2] || "").toLowerCase()] ?? 1);
  return Number.isFinite(x) ? x : null;
}

/** A check-size cell, where a bare small number is shorthand: "5" means $5M, "250" means $250K. */
function checkValue(num: string, unit: string): number {
  const x = parseFloat(num.replace(/,/g, ""));
  const u = UNIT[(unit || "").toLowerCase()];
  if (u) return x * u;
  if (x < 100) return x * 1e6;
  if (x < 1000) return x * 1e3;
  return x;
}

/** "$1-5M" | "5mm+" | "up to 10M" | "250k - 1m" -> [min, max] in dollars. */
export function parseMoneyRange(text: string | number | null | undefined): [number | null, number | null] {
  if (text == null || text === "") return [null, null];
  if (typeof text === "number") return Number.isFinite(text) ? [text, text] : [null, null];
  const hits = [...String(text).matchAll(MONEY)];
  if (!hits.length) return [null, null];
  const lastUnit = [...hits].reverse().find((h) => h[2])?.[2] || "";
  const vals = hits.map((h) => checkValue(h[1], h[2] || lastUnit));
  let lo: number | null = Math.min(...vals);
  let hi: number | null = Math.max(...vals);
  if (vals.length === 1 && /up to|max|</i.test(text)) lo = null;
  if (vals.length === 1 && /\+|min|at least|>/i.test(text)) hi = null;
  return [lo, hi];
}

function single(v: string | number | null | undefined): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? v : null;
  const [lo, hi] = parseMoneyRange(v);
  return lo ?? hi;
}

/** The check size someone has actually stated, and where. */
export function parseCheck(i: CategorizeInput): { min: number | null; max: number | null; source: "crm" | "notes" | ""; evidence: string } {
  let min = single(i.checkMinRaw);
  let max = single(i.checkMaxRaw);
  if (min != null || max != null) return { min, max, source: "crm", evidence: "CRM check columns" };
  if (i.checkRaw) {
    [min, max] = parseMoneyRange(i.checkRaw);
    if (min != null || max != null) return { min, max, source: "crm", evidence: `CRM check size '${i.checkRaw}'` };
  }
  const m = /writes (\$[\d.,]+\s*[KMB]?(?:\s*(?:-|to|–)\s*\$?[\d.,]+\s*[KMB]?)?) checks/i.exec(i.notes || "");
  if (m) {
    [min, max] = parseMoneyRange(m[1]);
    if (max == null) max = min;
    return { min, max, source: "notes", evidence: `Notes: '${m[0]}'` };
  }
  return { min: null, max: null, source: "", evidence: "" };
}

export function checkBandOf(min: number | null, max: number | null, type: string): CheckBand {
  if (NOT_INVESTORS.has(type)) return "Not an investor";
  const ref = max || min;
  if (!ref) return "Unknown";
  if (ref >= 10e6) return "$10M+";
  if (ref >= 2e6) return "$2M-$10M";
  if (ref >= 5e5) return "$500K-$2M";
  return "<$500K";
}

function firstType(text: string): [string, string] | null {
  if (!text) return null;
  for (const [t, rxs] of TYPE_RX)
    for (const r of rxs) {
      const m = r.exec(text);
      if (m) return [t, m[0]];
    }
  return null;
}

const normSector = (s: string): string | null => SECTORS.find((x) => x.toLowerCase() === s.trim().toLowerCase()) ?? null;

/** One sector from a phrase: the exact name, else the first sector whose keywords appear. */
export function matchSector(text: string): string | null {
  const exact = normSector(text);
  if (exact) return exact;
  for (const [s, rxs] of SECTOR_RX) if (rxs.some((r) => r.test(text))) return s;
  return null;
}

/** Every sector a phrase names ("cold chain or healthcare" -> both). */
function sectorsIn(text: string): string[] {
  const exact = normSector(text);
  if (exact) return [exact];
  return SECTOR_RX.filter(([, rxs]) => rxs.some((r) => r.test(text))).map(([s]) => s);
}

/* ------------------------------------------------------------------ touch-log helper */

const ASK_RX: RegExp[] = [
  /\bwants to see (?:anything|deals?|more|something) in ([^.;!?|]+)/i,
  /\basked (?:about|for) (?:deal flow|deals?|opportunities) (?:in|for) ([^.;!?|]+)/i,
  /\b(?:looking for|send me|send him|send her) (?:any |more |new )?(?:deals?|opportunities) in ([^.;!?|]+)/i,
  /\b(?:looking for|interested in|wants|any|asked about|asked for) (?:more |new |good )?([a-z&,' /-]{3,40}?) (?:deals?|opportunities)\b/i,
];

/** Sectors a person asked Peter for deals in, newest date per sector. Pass the whole touch log (inbound). */
export function askedSectorsFromTouches(touches: { date: string; summary: string; direction?: string }[]): { sector: string; date: string }[] {
  const latest = new Map<string, string>();
  for (const t of touches) {
    const text = t.summary || "";
    for (const rx of ASK_RX) {
      const m = rx.exec(text);
      if (!m) continue;
      for (const s of sectorsIn(m[1].trim())) if (!latest.has(s) || (t.date || "") > latest.get(s)!) latest.set(s, t.date || "");
    }
  }
  return [...latest].map(([sector, date]) => ({ sector, date }));
}

/* ------------------------------------------------------------------ the categorizer */

function findSignals(rules: Rule[], notes: string, touches: { date: string; summary: string }[]): Signal[] {
  const out: Signal[] = [];
  for (const rule of rules) {
    let hit: Signal | null = null;
    // notes first, then each touch
    for (const r of rule.rx) {
      const m = r.exec(notes);
      if (!m || (rule.selfOnly && RELATIVE_BEFORE.test(notes.slice(Math.max(0, m.index - 30), m.index)))) continue;
      const snip = around(notes, m.index, m.index + m[0].length);
      hit = { key: rule.key, label: rule.label, evidence: snip, date: monthDate(snip) };
      break;
    }
    if (!hit)
      for (const t of touches) {
        const text = t.summary || "";
        const m = rule.rx.map((r) => r.exec(text)).find((x) => x && !(rule.selfOnly && RELATIVE_BEFORE.test(text.slice(Math.max(0, x.index - 30), x.index))));
        if (!m) continue;
        hit = { key: rule.key, label: rule.label, evidence: text, date: monthDate(text) ?? (t.date || null) };
        break;
      }
    if (hit) out.push(hit);
  }
  return out;
}

export function categorize(i: CategorizeInput): CategorizeResult {
  const notes = i.notes || "";

  // sectors: strongest evidence first
  const sectors = new Map<string, string>();
  let fromCrm = false;
  for (const s of i.investedSectors || []) {
    const n = normSector(s);
    if (n && !sectors.has(n)) sectors.set(n, "invested in one of Peter's past deals");
  }
  for (const s of i.askedSectors || []) {
    const n = normSector(s);
    if (n && !sectors.has(n)) sectors.set(n, "asked Peter for deals in it");
  }
  const layers: [string, string][] = [
    ["CRM", i.crmSectors || ""],
    ["conference interest", (i.interests || []).join(" | ")],
    ["notes", notes],
    ["title/company", [i.title, i.company].filter(Boolean).join(" | ")],
  ];
  for (const [label, text] of layers) {
    if (!text.trim()) continue;
    for (const [s, rxs] of SECTOR_RX) {
      if (sectors.has(s)) continue;
      for (const r of rxs) {
        const m = r.exec(text);
        if (m) {
          sectors.set(s, `${label} '${m[0]}'`);
          if (label === "CRM") fromCrm = true;
          break;
        }
      }
    }
  }

  const check = parseCheck(i);

  // investor type
  let type = "Unknown";
  let typeEvidence = "";
  let typeSrc: FieldSource = "missing";
  const crm = (i.crmType || "").trim();
  const crmHit = crm ? (TYPE_SET.has(crm) && crm !== "Unknown" ? ([crm, crm] as [string, string]) : firstType(crm)) : null;
  if (crmHit) {
    type = crmHit[0];
    typeEvidence = `CRM type '${crm}'`;
    typeSrc = "crm";
  } else {
    for (const [label, text] of [["notes", notes], ["company", i.company || ""], ["title", i.title || ""]] as const) {
      const hit = firstType(text);
      if (hit) {
        type = hit[0];
        typeEvidence = `${label} '${hit[1]}'`;
        typeSrc = "notes";
        break;
      }
    }
  }
  if (type === "Unknown" && i.mondayGroup) {
    const h = GROUP_HINTS.find(([r]) => r.test(i.mondayGroup!));
    if (h) {
      type = h[1];
      // a group that mixes sponsors and funds: independent sponsors write the smaller checks
      if (type === "PE fund GP" && check.max != null && check.max < 5e6) type = "Independent sponsor";
      typeEvidence = `Monday group '${i.mondayGroup}'`;
      typeSrc = "crm";
    }
  }
  if (type === "Unknown")
    for (const reg of i.registrationTypes || []) {
      const h = REGISTRATION_HINTS.find(([r]) => r.test(reg));
      if (h) {
        type = h[1];
        typeEvidence = `conference registration '${reg}'`;
        typeSrc = "notes";
        break;
      }
    }
  // HNW vs UHNW is mostly about check size
  if ((type === "HNW individual" || type === "UHNW individual") && check.max != null) type = check.max >= 2e6 ? "UHNW individual" : "HNW individual";

  // check size: stated, else the type's usual range
  let checkMin = check.min ?? 0;
  let checkMax = check.max ?? check.min ?? 0;
  let checkSrc: string = check.source;
  if (!checkSrc) {
    if (TYPE_DEFAULT_CHECK[type]) {
      [checkMin, checkMax] = TYPE_DEFAULT_CHECK[type];
      checkSrc = "estimated from type";
    } else checkSrc = NOT_INVESTORS.has(type) ? "not an investor" : "unknown";
  }

  // private signals: notes, CRM status or group, touches
  const touches = i.recentTouches || [];
  const holds = findSignals(HOLD_RULES, notes, touches);
  const status = `${i.status || ""} ${i.mondayGroup || ""}`;
  if (/\bdo not contact\b|\bdnc\b|\bunsubscribed\b/i.test(status) && !holds.some((h) => h.key === "opted out"))
    holds.push({ key: "opted out", label: "Asked not to be contacted", evidence: `CRM status '${(i.status || i.mondayGroup || "").trim()}'`, date: null });
  else if (/\binactive\b|\barchived?\b/i.test(status) && !holds.some((h) => h.key === "inactive"))
    holds.push({ key: "inactive", label: "Marked inactive in the CRM", evidence: `CRM status '${(i.status || i.mondayGroup || "").trim()}'`, date: null });
  const whyNow = findSignals(WHY_NOW_RULES, notes, touches);

  const sectorHolds: CategorizeResult["sectorHolds"] = [];
  for (const [kind, r] of SECTOR_HOLD_RULES)
    for (const m of notes.matchAll(r)) {
      const s = matchSector(m[1]);
      if (s && !sectorHolds.some((x) => x.kind === kind && x.sector === s))
        sectorHolds.push({ sector: s, kind, evidence: around(notes, m.index!, m.index! + m[0].length), date: monthDate(around(notes, m.index!, m.index! + m[0].length + 12)) });
    }

  const negativePrefs = NEGATIVE_PREFS.filter(([, r]) => r.test(notes)).map(([k]) => k);

  return {
    type,
    sectors: [...sectors.keys()],
    checkMin: Math.round(checkMin),
    checkMax: Math.round(checkMax),
    checkBand: checkBandOf(check.min, check.max, type),
    holds,
    sectorHolds,
    whyNow,
    negativePrefs,
    evidence: { type: typeEvidence, sectors: [...sectors].map(([s, why]) => `${s}: ${why}`).join("; ") },
    sources: { type: typeSrc, sectors: sectors.size ? (fromCrm ? "crm" : "notes") : "missing", check: checkSrc },
  };
}
